/**
 * The visible label and optional description beside a checkbox or a switch.
 *
 * `Checkbox` and `Toggle` own the wiring (`htmlFor`, `aria-describedby`); this
 * owns the layout, so every labelled control in the app lines up the same way.
 *
 * @module components/ui/LabelledControl
 */

import React, { type ReactNode } from "react";

export interface LabelledControlProps {
  /** The control, already carrying `id` and `aria-describedby`. */
  children: ReactNode;
  /** `id` of the control, which the label points at. */
  controlId: string;
  label?: ReactNode;
  /** `id` the control's `aria-describedby` names. */
  descriptionId: string;
  description?: ReactNode;
  /** Centres the control on a one-line label instead of top-aligning it. */
  className?: string;
}

/** @internal Used by `Checkbox` and `Toggle`. */
export function LabelledControl({
  children,
  controlId,
  label = undefined,
  descriptionId,
  description = undefined,
  className = "",
}: LabelledControlProps): React.ReactElement {
  return (
    <div className={`flex items-start gap-3 text-sm ${className}`.trim()}>
      {children}
      <div className="min-w-0">
        {label === undefined ? null : (
          <label htmlFor={controlId} className="cursor-pointer font-medium">
            {label}
          </label>
        )}
        {description ? (
          <p id={descriptionId} className="text-muted">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}
